import React from 'react';

// Lazy load all feature components for code splitting
const ShippingCalculatorPage = React.lazy(() => import('../features/shipping-calculator/ShippingCalculatorPage'));
const DropshippingLanding = React.lazy(() => import('../features/dropshipping/DropshippingLanding'));
const ProductLibraryPage = React.lazy(() => import('../features/product-library/ProductLibraryPage'));
// Account feature
const AccountPage = React.lazy(() => import('../features/account/AccountPage'));
const ActualPaymentsUpload = React.lazy(() => import('../features/account/ActualPaymentsUpload'));
const UserAccount = React.lazy(() => import('../features/UserAccount'));
const AddressBookPage = React.lazy(() => import('../features/address-book/AddressBookPage'));

// Labeling feature default exports
const CreateLabel = React.lazy(() => import('../features/labeling/CreateLabel'));
const OrderHistory = React.lazy(() => import('../features/labeling/OrderHistory'));
const LabelManagement = React.lazy(() => import('../features/labeling/LabelManagement'));
const LabelLanding = React.lazy(() => import('../features/labeling/LabelLanding'));
const LabelingOverview = React.lazy(() => import('../features/labeling/LabelingOverview'));
const SelectAddress = React.lazy(() => import('../features/labeling/SelectAddress'));
const OrderMethod = React.lazy(() => import('../features/labeling/OrderMethod'));
const UploadAddress = React.lazy(() => import('../features/labeling/UploadAddress'));
const ConfirmBatchAddress = React.lazy(() => import('../features/labeling/ConfirmBatchAddress'));
const ProcessAddress = React.lazy(() => import('../features/labeling/ProcessAddress'));
const AddAddress = React.lazy(() => import('../features/labeling/AddAddress'));
const AddRecipientAddress = React.lazy(() => import('../features/labeling/AddRecipientAddress'));
const SelectCourier = React.lazy(() => import('../features/labeling/SelectCourier'));
const SelectDeliveryTime = React.lazy(() => import('../features/labeling/SelectDeliveryTime'));
const LabelProductInfo = React.lazy(() => import('../features/labeling/LabelProductInfo'));
// TODO(i18n-cleanup): Remove legacy payment route/component after deprecation and traffic validation.
const PaymentPage = React.lazy(() => import('../features/labeling/PaymentPage'));
const PreviewOrder = React.lazy(() => import('../features/labeling/PreviewOrder'));
const OrderLabelDetailsPage = React.lazy(() => import('../features/labeling/OrderLabelDetailsPage'));
const OrderHistoryDetail = React.lazy(() => import('../features/labeling/OrderHistoryDetail'));
const OrderTrackingInfo = React.lazy(() => import('../features/labeling/OrderTrackingInfo'));
const TrackingInfo = React.lazy(() => import('../features/labeling/TrackingInfo'));
const LabelAddProduct = React.lazy(() => import('../features/labeling/LabelAddProduct'));
const InquiryAddress = React.lazy(() => import('../features/labeling/InquiryAddress'));
const InquiryOverview = React.lazy(() => import('../features/labeling/InquiryOverview'));
const LabelRating = React.lazy(() => import('../features/labeling/LabelRating'));

// Admin features
const AdminLanding = React.lazy(() => import('../features/admin/AdminLanding'));
const Front = React.lazy(() => import('../features/admin/Front').then(module => ({ default: module.Front })));
const WeChatCallback = React.lazy(() => import('../features/labeling/WeChatCallback'));
const PriceMatrixUpload = React.lazy(() => import('../features/admin/PriceMatrixUpload'));
const ExtraFeeConfigUpload = React.lazy(() => import('../features/admin/ExtraFeeConfigUpload'));
// E-commerce feature
const ECommercePage = React.lazy(() => import('../features/e-commerce/ECommercePage'));
const ECommerceWmsPage = React.lazy(() => import('../features/e-commerce/WmsUploadPage'));
const ECommerceEvalPage = React.lazy(() => import('../features/e-commerce/EvalPage'));
// ERP feature
const ErpPage = React.lazy(() => import('../features/erp/ErpPage'));
// PodOverview component not found, omitting for now
// const PodOverview = React.lazy(() => import('../features/pod/PodOverview'));

export interface RouteConfig {
    key: string;
    matcher: (path: string) => boolean;
    component: React.ComponentType;
}

export const ROUTE_REGISTRY: RouteConfig[] = [
    { key: 'calculator', matcher: (p) => p === '/', component: ShippingCalculatorPage },
    { key: 'dropshipping', matcher: (p) => p.startsWith('/dropshipping'), component: DropshippingLanding },
    { key: 'products', matcher: (p) => p.startsWith('/products'), component: ProductLibraryPage },
    { key: 'addressBook', matcher: (p) => p.startsWith('/address-book'), component: AddressBookPage },
    { key: 'labelingLanding', matcher: (p) => p === '/shipping', component: LabelLanding },
    { key: 'labelingOverview', matcher: (p) => p === '/labeling-overview', component: LabelingOverview },
    { key: 'labelManagement', matcher: (p) => p.startsWith('/label-management'), component: LabelManagement },
    { key: 'createLabel', matcher: (p) => p.startsWith('/labeling/create-label'), component: CreateLabel },
    { key: 'orderHistoryList', matcher: (p) => p === '/labeling-orderHistory', component: OrderHistory },
    { key: 'account', matcher: (p) => p.startsWith('/account'), component: AccountPage },
    { key: 'userProfile', matcher: (p) => p.startsWith('/user/profile'), component: UserAccount },
    // Note: /labeling-reloadStatus, /labeling-myAccount removed - moved to clean /account/* paths
    { key: 'selectAddress', matcher: (p) => p.startsWith('/labeling-selectAddress'), component: SelectAddress },
    { key: 'orderMethod', matcher: (p) => p.startsWith('/labeling-orderMethod'), component: OrderMethod },
    { key: 'uploadAddress', matcher: (p) => p.startsWith('/labeling-uploadAddress'), component: UploadAddress },
    { key: 'confirmBatchAddress', matcher: (p) => p.startsWith('/labeling-confirmBatchAddress'), component: ConfirmBatchAddress },
    { key: 'processAddress', matcher: (p) => p.startsWith('/labeling-processAddress'), component: ProcessAddress },
    { key: 'addAddress', matcher: (p) => p.startsWith('/labeling-addAddress'), component: AddAddress },
    { key: 'addRecipientAddress', matcher: (p) => p.startsWith('/labeling-addRecipientAddress'), component: AddRecipientAddress },
    { key: 'selectCourier', matcher: (p) => p.startsWith('/labeling-selectCourier'), component: SelectCourier },
    { key: 'selectDeliveryTime', matcher: (p) => p.startsWith('/labeling-selectDeliveryTime'), component: SelectDeliveryTime },
    { key: 'labelProductInfo', matcher: (p) => p.startsWith('/labeling-productInfo'), component: LabelProductInfo },
    // TODO(i18n-cleanup): Remove this route when legacy payment flow is fully retired.
    { key: 'paymentPage', matcher: (p) => p.startsWith('/labeling/payment/'), component: PaymentPage },
    { key: 'previewOrder', matcher: (p) => p.startsWith('/labeling-previewOrder'), component: PreviewOrder },
    { key: 'orderLabelDetails', matcher: (p) => p.startsWith('/labeling-orderSuccess'), component: OrderLabelDetailsPage },
    { key: 'actualPayments', matcher: (p) => p.startsWith('/labeling-actual-payments-upload'), component: ActualPaymentsUpload },
    { key: 'orderHistoryDetail', matcher: (p) => /^\/labeling-orderHistory\/[^/]+$/.test(p), component: OrderHistoryDetail },
    { key: 'orderTracking', matcher: (p) => p.includes('/package/'), component: OrderTrackingInfo },
    { key: 'trackingInfo', matcher: (p) => p.startsWith('/labeling-trackingInfo'), component: TrackingInfo },
    { key: 'labelAddProduct', matcher: (p) => p.startsWith('/labeling-addProduct'), component: LabelAddProduct },
    { key: 'inquiryAddress', matcher: (p) => p.startsWith('/labeling-inquiryAddress'), component: InquiryAddress },
    { key: 'inquiryOverview', matcher: (p) => p.startsWith('/labeling-inquiryOverview'), component: InquiryOverview },
    {
        key: 'admin',
        matcher: (p) => p.startsWith('/admin') ||
            p.startsWith('/labeling-admin') ||
            p.startsWith('/labeling-billing') ||
            p.startsWith('/labeling-sales-dashboard'),
        component: AdminLanding
    },
    { key: 'labelRating', matcher: (p) => p.startsWith('/labeling/rating'), component: LabelRating },
    // Admin uploads
    { key: 'priceMatrixUpload', matcher: (p) => p === '/admin/price-matrix-upload', component: PriceMatrixUpload },
    { key: 'extraFeeConfigUpload', matcher: (p) => p === '/admin/extra-fee-config-upload', component: ExtraFeeConfigUpload },
    // WeChat Auth
    {
        key: 'wechatLoginCallback',
        matcher: (p) => p === '/auth/wechat/callback',
        component: () => React.createElement(WeChatCallback, { mode: 'login' })
    },
    {
        key: 'wechatBindCallback',
        matcher: (p) => p === '/auth/wechat/bind-callback',
        component: () => React.createElement(WeChatCallback, { mode: 'bind' })
    },
    { key: 'ecommerceEval', matcher: (p) => p.startsWith('/e-commerce-eval'), component: ECommerceEvalPage },
    { key: 'ecommerceWms', matcher: (p) => p.startsWith('/e-commerce-wms'), component: ECommerceWmsPage },
    // E-commerce
    { key: 'ecommerce', matcher: (p) => p.startsWith('/e-commerce'), component: ECommercePage },
    // Showcase page
    { key: 'front', matcher: (p) => p === '/front', component: Front },
    // ERP
    { key: 'erp', matcher: (p) => p.startsWith('/erp'), component: ErpPage },
];
